import React from 'react';
import { StudentPublicProfile } from '../../../types';
import '../styles/StudentSelector.css';
import '../../muses/styles/MuseTiers.css';

interface StudentSelectorProps {
  students: StudentPublicProfile[];
  onlineStudentIds: string[];
  squadName: string;
  onSelectStudent: (studentId: string) => void;
  onBack: () => void;
  loading: boolean;
  searchValue: string;
}

const StudentSelector: React.FC<StudentSelectorProps> = ({ 
  students, 
  onlineStudentIds, 
  squadName, 
  onSelectStudent, 
  onBack,
  loading,
  searchValue
}) => {
  // Determine grid class based on number of students
  const getGridClass = (studentCount: number) => {
    if (studentCount <= 12) {
      return `students-${studentCount}`;
    }
    return 'students-many';
  };

  const gridClass = getGridClass(students.length);

  const filteredStudents = students.filter(student => {
    const displayName = student.displayName?.toLowerCase() || '';
    const email = student.email?.toLowerCase() || '';
    const searchTerm = searchValue.toLowerCase();
    return displayName.includes(searchTerm) || email.includes(searchTerm);
  });

  return (
    <div className="gaming-lobby">
      {/* Animated background elements */}
      <div className="lobby-bg-effects">
        <div className="floating-particle"></div>
        <div className="floating-particle"></div>
        <div className="floating-particle"></div>
        <div className="floating-particle"></div>
        <div className="floating-particle"></div>
      </div>

      {/* Header section */}
      <div className="lobby-header">
        <button onClick={onBack} className="lobby-back-btn">
          <span className="back-icon">←</span>
          <span>BACK</span>
        </button>
        
        <div className="squad-info">
          <div className="squad-status">
            <div className="status-indicator"></div>
            <span>SQUAD FOUND</span>
          </div>
          <h1 className="squad-title">{squadName}</h1>
          <div className="squad-subtitle">Choose your Muse and join the adventure!</div>
        </div>

        <div className="lobby-stats">
          <div className="stat-item">
            <span className="stat-number">{students.length}</span>
            <span className="stat-label">PLAYERS</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{onlineStudentIds.length}</span>
            <span className="stat-label">ONLINE</span>
          </div>
        </div>
      </div>

      {/* Player selection area */}
      <div className="player-selection-area">
        <h2 className="selection-title">
          <span className="title-accent">SELECT</span>
          <span className="title-main">YOUR PLAYER</span>
        </h2>

        <div className={`players-grid ${gridClass}`}>
          {filteredStudents.map((student, index) => {
            const isOnline = onlineStudentIds.includes(student.id);
            
            return (
              <div
                key={student.id}
                className={`player-card ${isOnline ? 'player-online' : 'player-available'} ${loading ? 'player-loading' : ''}`}
                onClick={() => !isOnline && !loading && onSelectStudent(student.id)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="card-hover-effect"></div>
                
                {/* Card Header - Status + Name */}
                <div className="player-card-header">
                  <div className="player-name-header">{student.displayName}</div>
                  {/* Only show status if name is short enough to avoid text conflict */}
                  {student.displayName.length <= 6 && (
                    <div className="player-status-header">
                      {isOnline ? (
                        <div className="status-online">
                          <div className="pulse-dot"></div>
                          <span>IN GAME</span>
                        </div>
                      ) : (
                        <div className="status-available">
                          <div className="ready-dot"></div>
                          <span>READY</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Body - Muse Avatar */}
                <div className="player-card-body">
                  <div className="player-avatar">
                    {student.selectedMuse && typeof student.selectedMuse === 'object' && student.selectedMuse.img ? (
                      <div className="avatar-frame">
                        <div className={`rarity-glow ${student.selectedMuse.rarity?.toLowerCase() || 'common'}`}></div>
                        <div className={`custom-muse-display ${student.selectedMuse.tier ? `muse-tier-${student.selectedMuse.tier}` : ''}`}>
                          <img
                            src={student.selectedMuse.img}
                            alt={student.selectedMuse.name || 'Muse'}
                            className="muse-avatar-img"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '50%'
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="avatar-frame">
                        <div className="locked-overlay">?</div>
                        <div className="custom-muse-display">
                          <div className="placeholder-muse">?</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer - Action Button */}
                <div className="player-card-footer">
                  <button 
                    className={`action-button ${isOnline || loading ? 'disabled' : ''}`}
                    disabled={isOnline || loading}
                  >
                    {isOnline ? 'PLAYER IN GAME' : 'TAP TO JOIN'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="lobby-loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">
            JOINING SQUAD
            <span className="loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentSelector; 